/**
 * Abstract inline SVG "chart" cover images — replace generic stock photos.
 * Uses only the navy palette + subtle white grid so we stay on-brand.
 * Variants: line | candle | wave | bars | network | portfolio
 */
const NAVY = "#0A2540";
const NAVY_SOFT = "#183454";
const GRID = "rgba(255,255,255,0.08)";
const GOLD = "#C6A15B";

const COVER_IMAGES = {
  "investment-banking": "/covers/ib-cover.png",
  "private-equity": "/covers/pe-cover.png",
  "venture-capital": "/covers/vc-cover.png",
  "hedge-funds": "/covers/hf-cover.png",
  "energy": "/covers/energy-cover.png",
  "real-estate": "/covers/ref-cover.png",
};

export default function ChartCover({ variant = "line", label, className = "", slug }) {
  const cover = slug ? COVER_IMAGES[slug] : null;
  if (cover) {
    return (
      <div className={`relative w-full h-full overflow-hidden ${className}`} aria-hidden>
        <img src={cover} alt="" className="w-full h-full object-cover" loading="lazy" />
      </div>
    );
  }
  return (
    <div className={`relative w-full h-full overflow-hidden ${className}`} aria-hidden>
      <svg viewBox="0 0 600 340" preserveAspectRatio="xMidYMid slice" className="w-full h-full">
        <defs>
          <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={NAVY} />
            <stop offset="100%" stopColor={NAVY_SOFT} />
          </linearGradient>
          <linearGradient id="glow" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#7db2ff" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#7db2ff" stopOpacity="0" />
          </linearGradient>
          <pattern id="grid" width="30" height="30" patternUnits="userSpaceOnUse">
            <path d="M 30 0 L 0 0 0 30" fill="none" stroke={GRID} strokeWidth="1" />
          </pattern>
        </defs>

        <rect width="600" height="340" fill="url(#bg)" />
        <rect width="600" height="340" fill="url(#grid)" opacity="0.6" />

        {variant === "line" && <LineChart />}
        {variant === "candle" && <CandleChart />}
        {variant === "wave" && <WaveChart />}
        {variant === "bars" && <BarChart />}
        {variant === "network" && <NetworkChart />}
        {variant === "portfolio" && <PortfolioChart />}

        {label && (
          <text
            x="30"
            y="310"
            fill="rgba(255,255,255,0.55)"
            fontSize="10"
            letterSpacing="4"
            fontFamily="Manrope, sans-serif"
          >
            {label.toUpperCase()}
          </text>
        )}
      </svg>
    </div>
  );
}

function LineChart() {
  const path =
    "M 20 240 L 90 210 L 150 220 L 210 170 L 260 190 L 320 130 L 380 155 L 440 90 L 500 110 L 580 60";
  return (
    <g>
      <path d={`${path} L 580 340 L 20 340 Z`} fill="url(#glow)" />
      <path d={path} fill="none" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" />
      <circle cx="580" cy="60" r="4" fill={GOLD} />
    </g>
  );
}

function CandleChart() {
  const bars = [
    { x: 40, o: 200, c: 180, h: 160, l: 220, up: true },
    { x: 100, o: 180, c: 210, h: 165, l: 230, up: false },
    { x: 160, o: 210, c: 170, h: 140, l: 235, up: true },
    { x: 220, o: 170, c: 200, h: 155, l: 220, up: false },
    { x: 280, o: 200, c: 160, h: 130, l: 220, up: true },
    { x: 340, o: 160, c: 190, h: 150, l: 210, up: false },
    { x: 400, o: 190, c: 130, h: 110, l: 210, up: true },
    { x: 460, o: 130, c: 170, h: 100, l: 190, up: false },
    { x: 520, o: 170, c: 100, h: 85, l: 190, up: true },
  ];
  return (
    <g>
      {bars.map((b, i) => {
        const color = b.up ? "#7db2ff" : "rgba(255,255,255,0.55)";
        const y = Math.min(b.o, b.c);
        const height = Math.max(6, Math.abs(b.o - b.c));
        return (
          <g key={i}>
            <line x1={b.x} y1={b.h} x2={b.x} y2={b.l} stroke={color} strokeWidth="1" />
            <rect x={b.x - 8} y={y} width="16" height={height} fill={color} />
          </g>
        );
      })}
    </g>
  );
}

function WaveChart() {
  return (
    <g>
      <path
        d="M 0 200 C 90 120 180 260 300 200 C 420 140 510 280 600 200"
        fill="none"
        stroke="#ffffff"
        strokeOpacity="0.8"
        strokeWidth="2"
      />
      <path
        d="M 0 240 C 90 180 180 300 300 240 C 420 180 510 300 600 240"
        fill="none"
        stroke="#7db2ff"
        strokeOpacity="0.7"
        strokeWidth="1.5"
      />
      <path
        d="M 0 170 C 100 110 200 220 300 170 C 420 120 500 220 600 170"
        fill="none"
        stroke={GOLD}
        strokeOpacity="0.7"
        strokeWidth="1.2"
      />
    </g>
  );
}

function BarChart() {
  const bars = [180, 140, 200, 120, 90, 160, 100, 70, 130, 60];
  return (
    <g>
      {bars.map((h, i) => (
        <rect
          key={i}
          x={40 + i * 55}
          y={280 - h}
          width="30"
          height={h}
          rx="4"
          fill={i === bars.length - 1 ? GOLD : "rgba(255,255,255,0.6)"}
          opacity={i === bars.length - 1 ? 1 : 0.7 + i * 0.02}
        />
      ))}
    </g>
  );
}

function NetworkChart() {
  const nodes = [
    { x: 90, y: 200 },
    { x: 190, y: 130 },
    { x: 230, y: 250 },
    { x: 330, y: 180 },
    { x: 420, y: 100 },
    { x: 470, y: 240 },
    { x: 540, y: 160 },
  ];
  const edges = [
    [0, 1], [0, 2], [1, 3], [2, 3], [3, 4], [3, 5], [4, 6], [5, 6], [1, 4],
  ];
  return (
    <g>
      {edges.map(([a, b], i) => (
        <line
          key={i}
          x1={nodes[a].x}
          y1={nodes[a].y}
          x2={nodes[b].x}
          y2={nodes[b].y}
          stroke="rgba(255,255,255,0.35)"
          strokeWidth="1"
        />
      ))}
      {nodes.map((n, i) => (
        <circle
          key={i}
          cx={n.x}
          cy={n.y}
          r={i === 3 ? 7 : 4}
          fill={i === 3 ? GOLD : "#ffffff"}
          opacity={i === 3 ? 1 : 0.85}
        />
      ))}
    </g>
  );
}

function PortfolioChart() {
  // Represents a PE fund's portfolio: a grid of company "tiles" (buyouts) with a
  // rising value-creation curve overlay. One tile is highlighted in gold to signal
  // an active acquisition / value inflection.
  const tiles = [
    { x: 40, y: 90, w: 78, h: 54, active: false },
    { x: 138, y: 90, w: 78, h: 54, active: false },
    { x: 236, y: 90, w: 78, h: 54, active: true },
    { x: 334, y: 90, w: 78, h: 54, active: false },
    { x: 432, y: 90, w: 78, h: 54, active: false },
    { x: 40, y: 168, w: 78, h: 54, active: false },
    { x: 138, y: 168, w: 78, h: 54, active: false },
    { x: 236, y: 168, w: 78, h: 54, active: false },
    { x: 334, y: 168, w: 78, h: 54, active: false },
    { x: 432, y: 168, w: 78, h: 54, active: false },
  ];
  const arrow = "M 40 260 C 140 250 220 220 300 200 C 380 180 460 130 560 100";
  return (
    <g>
      {tiles.map((t, i) => (
        <g key={i}>
          <rect
            x={t.x}
            y={t.y}
            width={t.w}
            height={t.h}
            rx="6"
            fill={t.active ? "#C6A15B" : "rgba(255,255,255,0.08)"}
            stroke={t.active ? "#C6A15B" : "rgba(255,255,255,0.28)"}
            strokeWidth="1"
          />
          <line x1={t.x + 10} y1={t.y + 16} x2={t.x + t.w - 10} y2={t.y + 16} stroke={t.active ? "rgba(10,37,64,0.6)" : "rgba(255,255,255,0.35)"} strokeWidth="1.2" />
          <line x1={t.x + 10} y1={t.y + 26} x2={t.x + t.w - 20} y2={t.y + 26} stroke={t.active ? "rgba(10,37,64,0.5)" : "rgba(255,255,255,0.25)"} strokeWidth="1" />
          <line x1={t.x + 10} y1={t.y + 36} x2={t.x + t.w - 28} y2={t.y + 36} stroke={t.active ? "rgba(10,37,64,0.4)" : "rgba(255,255,255,0.18)"} strokeWidth="1" />
        </g>
      ))}
      <path d={arrow} fill="none" stroke="#ffffff" strokeOpacity="0.85" strokeWidth="2" strokeLinecap="round" />
      <circle cx="560" cy="100" r="4" fill={GOLD} />
    </g>
  );
}

